/* eslint-disable @typescript-eslint/no-use-before-define */
import { Option, some, none, fold } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';

export interface TermLength {
    years: number;
}
class OneYearTermLength implements TermLength {
    readonly years = 1;
}
class ThreeYearTermLength implements TermLength {
    readonly years = 3;
}
class SixYearTermLength implements TermLength {
    readonly years = 6;
}
export const oneYearTerm = new OneYearTermLength();
export const threeYearTerm = new ThreeYearTermLength();
export const sixYearTerm = new SixYearTermLength();

export const allTermLengths = [oneYearTerm, threeYearTerm, sixYearTerm];

export function buildTermLengthMap() {
    const m = new Map<number, TermLength>();
    for (const tl of allTermLengths) {
        m.set(tl.years, tl);
    }
    return m;
}

export class InvestmentStrategyType {
    readonly code: string;
    readonly label: string;
    constructor(code: string, label: string) {
        this.code = code;
        this.label = label;
    }
}

export const tieredParticipationRate = new InvestmentStrategyType('tieredParticipation', 'Tiered Participation Rate')
export const pointToPointCapRate = new InvestmentStrategyType('pointToPointCap', 'Point to Point Cap Rate')
export const stepRate = new InvestmentStrategyType('step', 'Step Rate Plus')
export const dualDirection = new InvestmentStrategyType('dualDirection', 'Dual Directional Cap Rate')

export const allInvestmentStrategyTypes = [tieredParticipationRate, pointToPointCapRate, stepRate, dualDirection];

export function buildInvestmentStrategyTypeMap() {
    const m = new Map<string, InvestmentStrategyType>();
    for (const strategyType of allInvestmentStrategyTypes) {
        m.set(strategyType.code, strategyType);
    }
    return m;
}


export interface InvestmentBuffer {
    percentage: number;
    code: string;
}
class Buffer5 implements InvestmentBuffer {
    readonly percentage = 5;
    readonly code = this.percentage.toString();
}
class Buffer10 implements InvestmentBuffer {
    readonly percentage = 10;
    readonly code = this.percentage.toString();
}
class Buffer15 implements InvestmentBuffer {
    readonly percentage = 15;
    readonly code = this.percentage.toString();
}
class Buffer20 implements InvestmentBuffer {
    readonly percentage = 20;
    readonly code = this.percentage.toString();
}
class Buffer100 implements InvestmentBuffer {
    readonly percentage = 100;
    readonly code = this.percentage.toString();
}
export const fivePercentBuffer = new Buffer5();
export const tenPercentBuffer = new Buffer10();
export const fifteenPercentBuffer = new Buffer15();
export const twentyPercentBuffer = new Buffer20();
export const oneHundredPercentBuffer = new Buffer100();

export const allBuffers = [fivePercentBuffer, tenPercentBuffer, fifteenPercentBuffer, twentyPercentBuffer, oneHundredPercentBuffer];

export function buildInvestmentBufferMap() {
    const m = new Map<number, InvestmentBuffer>();
    for (const invBuf of allBuffers) {
        m.set(invBuf.percentage, invBuf);
    }
    return m;
}

export interface InvestmentIndex {
    code: string;
    label: string;
}
class SP500 implements InvestmentIndex {
    readonly code = 'sp500';
    readonly label = 'S&P 500';
}
class MsciEafe implements InvestmentIndex {
    readonly code = 'msci-eafe';
    readonly label = 'MSCI EAFE';
}
class InvescoQQQETF implements InvestmentIndex {
    readonly code = 'invesco';
    readonly label = 'Invesco QQQ ETF';
}
class ISharesRussell2000ETF implements InvestmentIndex {
    readonly code = 'russell';
    readonly label = 'iShares&reg; Russell 2000 ETF';
}

export const sp500 = new SP500();
export const msciEafe = new MsciEafe();
export const invesco = new InvescoQQQETF();
export const russell = new ISharesRussell2000ETF();

export const allIndices = [sp500, msciEafe, invesco, russell];

export function buildInvestmentIndexMap() {
    const m = new Map<string, InvestmentIndex>();
    for (const invIdx of allIndices) {
        m.set(invIdx.code, invIdx);
    }
    return m;
}



export class InvestmentStrategy {
    strategyType: Option<InvestmentStrategyType>;
    buffer: Option<InvestmentBuffer>;
    investmentIndex: Option<InvestmentIndex>;
    constructor(strategyType: Option<InvestmentStrategyType>,
        buffer: Option<InvestmentBuffer>,
        index: Option<InvestmentIndex>) {
        this.strategyType = strategyType;
        this.buffer = buffer;
        this.investmentIndex = index;
    }
    withStrategyType(strategyType: InvestmentStrategyType): InvestmentStrategy {
        return new InvestmentStrategy(some(strategyType), this.buffer, this.investmentIndex);
    }
    withoutStrategyType(): InvestmentStrategy {
        return new InvestmentStrategy(none, this.buffer, this.investmentIndex);
    }

    withBuffer(buffer: InvestmentBuffer): InvestmentStrategy {
        return new InvestmentStrategy(this.strategyType, some(buffer), this.investmentIndex);
    }
    withoutBuffer(): InvestmentStrategy {
        return new InvestmentStrategy(this.strategyType, none, this.investmentIndex);
    }


    withInvestmentIndex(index: InvestmentIndex): InvestmentStrategy {
        return new InvestmentStrategy(this.strategyType, this.buffer, some(index));
    }
    withoutInvestmentIndex(): InvestmentStrategy {
        return new InvestmentStrategy(this.strategyType, this.buffer, none);
    }
}

export class AllocatedStrategy {
    strategy: InvestmentStrategy;
    allocation: Option<number>;
    customStartYear: Option<number>;

    constructor(strategy: InvestmentStrategy,
        allocation: Option<number>,
        startYear: Option<number>) {
        this.strategy = strategy;
        this.allocation = allocation;
        this.customStartYear = startYear;
    }
    withStrategy(strategy: InvestmentStrategy): AllocatedStrategy {
        return new AllocatedStrategy(strategy, this.allocation, this.customStartYear);
    }
    withAllocation(allocation: number): AllocatedStrategy {
        return new AllocatedStrategy(this.strategy, some(allocation), this.customStartYear);
    }
    withoutAllocation(): AllocatedStrategy {
        return new AllocatedStrategy(this.strategy, none, this.customStartYear);
    }
    withCustomStartYear(startYear: number): AllocatedStrategy {
        return new AllocatedStrategy(this.strategy, this.allocation, some(startYear));
    }
    withoutCustomStartYear(): AllocatedStrategy {
        return new AllocatedStrategy(this.strategy, this.allocation, none);
    }
}

export class StrategyList {
    static MAX_STRATEGIES = 3;
    private allocatedStrategies: Array<AllocatedStrategy>;

    constructor(allocatedStrategies: Array<AllocatedStrategy>) {
        if (allocatedStrategies.length === 0) {
            this.allocatedStrategies = [new AllocatedStrategy(new InvestmentStrategy(none, none, none), none, none)];
        } else {
            this.allocatedStrategies = allocatedStrategies.slice(0, StrategyList.MAX_STRATEGIES);
        }
    }

    getAllocatedStrategy(index: number): AllocatedStrategy {
        if (index < 0 || index >= this.allocatedStrategies.length) {
            throw new RangeError(`index ${index} does not refer to an element in the allocated strategy list (length ${this.allocatedStrategies.length})}`)
        }
        return this.allocatedStrategies[index];
    }

    getStrategyCount(): number {
        return this.allocatedStrategies.length;
    }

    getTotalAllocation(): number {
        const values = this.allocatedStrategies.map((as) => pipe(as.allocation, fold(() => 0, (n) => n)));
        return values.reduce((x, y) => x + y)
    }

    withAllocatedStrategyAt(index: number, allocation: AllocatedStrategy): StrategyList {
        if (index < 0 || index >= this.allocatedStrategies.length || index >= StrategyList.MAX_STRATEGIES) {
            throw new RangeError(`Cannot set allocated strategy at index ${index}; current length is ${this.allocatedStrategies.length}`);
        } else {
            const newAllocatedStrategies = [...this.allocatedStrategies];
            newAllocatedStrategies[index] = allocation;
            return new StrategyList(newAllocatedStrategies);
        }
    }

    addAllocatedStrategy(allocation: AllocatedStrategy): StrategyList {
        if (this.allocatedStrategies.length >= StrategyList.MAX_STRATEGIES) {
            throw new RangeError(`Cannot add allocated strategy; current length is ${this.allocatedStrategies.length}`);
        } else {
            return new StrategyList([...this.allocatedStrategies, allocation])
        }
    }

    withStrategyAllocationRemovedAtIndex(index: number): StrategyList {
        if (index === 0) {
            throw new RangeError("Cannot remove first strategy");
        } else {
            if (index >= this.allocatedStrategies.length) {
                throw new RangeError(`index ${index} does not refer to a strategy (total number ${this.allocatedStrategies.length})`);
            } else {
                const newStrategies = [...this.allocatedStrategies.slice()];
                newStrategies.splice(index, 1);
                return new StrategyList(newStrategies);
            }
        }
    }

}

export class ExportReport {
    readonly clientName: Option<string>;
    readonly financialProfessionalName: Option<string>;
    readonly clientEmail: Option<string>;

    constructor(clientName: Option<string>,
        financialProfessionalName: Option<string>,
        clientEmail: Option<string>) {
        this.clientName = clientName;
        this.financialProfessionalName = financialProfessionalName;
        this.clientEmail = clientEmail;
    }
    withClientName(name: string) {
        return new ExportReport(some(name), this.financialProfessionalName, this.clientEmail);
    }
    withoutClientName() {
        return new ExportReport(none, this.financialProfessionalName, this.clientEmail);
    }
    withFinancialProfessionalName(name: string) {
        return new ExportReport(this.clientName, some(name), this.clientEmail);
    }
    withoutFinancialProfessionalName() {
        return new ExportReport(this.clientName, none, this.clientEmail);
    }
    withClientEmail(email: string) {
        return new ExportReport(this.clientName, this.financialProfessionalName, some(email));
    }
    withoutClientEmail() {
        return new ExportReport(this.clientName, this.financialProfessionalName, none);
    }
}

export class CalculatorInputs {

    readonly premiumPayment: Option<number>;
    readonly term: Option<TermLength>;
    readonly strategyList: StrategyList;
    readonly exportReport: ExportReport;

    constructor(premiumPayment: Option<number>,
        term: Option<TermLength>,
        strategyList: StrategyList,
        exportReport: ExportReport) {
        this.premiumPayment = premiumPayment;
        this.term = term;
        this.strategyList = strategyList;
        this.exportReport = exportReport;
    }

    withPremiumPayment(amount: number): CalculatorInputs {
        return new CalculatorInputs(some(amount), this.term, this.strategyList, this.exportReport);
    }
    withoutPremiumPayment(): CalculatorInputs {
        return new CalculatorInputs(none, this.term, this.strategyList, this.exportReport);
    }

    withTerm(term: TermLength): CalculatorInputs {
        return new CalculatorInputs(this.premiumPayment, some(term), this.strategyList, this.exportReport);
    }
    withoutTerm(): CalculatorInputs {
        return new CalculatorInputs(this.premiumPayment, none, this.strategyList, this.exportReport);
    }

    withStrategyList(list: StrategyList): CalculatorInputs {
        return new CalculatorInputs(this.premiumPayment, this.term, list, this.exportReport);
    }

    withExportReport(exportReport: ExportReport) {
        return new CalculatorInputs(this.premiumPayment, this.term, this.strategyList, exportReport);
    }
}

export const initialExportReport = new ExportReport(none, none, none);

export const initialCalculatorInputs: CalculatorInputs = new CalculatorInputs(none, none, new StrategyList([]), initialExportReport);


